import { UploaderBase, UploaderList } from '@cloudike/web_core'
import { createAsyncThunk, createEntityAdapter, createSlice, } from '@reduxjs/toolkit'
import { NOTIFICATION_TYPES, showNotification } from 'features/common/notifications'
import { fetchFilesPublicLinkItems } from 'features/public-link-files/model/slice/publicLinkFilesSlice'
import { fetchFirstRecentlyUploadedThunk } from 'features/assets/model/slice/assetsSlice'
import { RootState } from 'shared/config/store'
import { getFilesSdk } from 'shared/sdk/files'
import { converFileListToFileObjectList } from 'shared/utils/convertFileListToFileObjectList'
import { generateFilesItemName, toMb } from 'shared/utils/filesUtils'
import { BatchedOpSchema, searchApi, SearchNode } from 'shared/api/searchApi'
import { closeModal, openModal } from 'features/common/model/slice/moadlSlice'
import { BoardToolbarAction } from 'shared/constants/toolbar'
import { DuplicateActionChoice } from 'features/files-uploading/ui/DuplicateActionModal'

import { ROUTES } from '../../../../shared/constants/routesPaths'
import { NodeTypes } from '../../../../shared/constants/common'

interface IUploadingFile {
  name: string
  size: string | number
  type: string
  state: any
  id?: string | number
}

const UPLOAD_BATCH_SIZE = 1

let filesUploaderListObject: UploaderList | undefined
let sharedFilesUploaderListObject: UploaderList | undefined

const UPLOADED_ITEM_INITIAL_STATE = {
  isUploading: false,
  isCompleted: false,
  isError: false,
  progress: 0,
  error: null,
}

enum UPLOADER_LIST_EVENTS {
  UPDATE = 'update',
  ERROR = 'error',
}

export enum UPLOADING_STATUSES {
  IDLE = 'IDLE',
  IN_PROGRESS = 'IN_PROGRESS',
  FINISHED = 'FINISHED',
  ERROR = 'ERROR'
}

export enum UPLOADER_LIST_TYPES {
  FILES = 'FILES',
  VERSIONS = 'VERSIONS',
  SHARED_WITH_ME_FILES = 'SHARED_WITH_ME_FILES',
  SHARED_ALBUM = 'SHARED_ALBUM',
  FAMILY_TIMELINE_COLLABORATOR = 'FAMILY_TIMELINE_COLLABORATOR',
  FAMILY_ALBUM_COLLABORATOR = 'FAMILY_ALBUM_COLLABORATOR',
  AI_ASSISTANT_ATTACHMENTS = 'AI_ASSISTANT_ATTACHMENTS'
}

interface IExtendedUploadingFile extends IUploadingFile {
  uploadingSourceType: UPLOADER_LIST_TYPES,
  uploadedItem: any,
  uploadId?: string,
  id: string,
  name: string,
  parentId?: string,
  previewUrl?: string,
  nodeId?: string,
}

const tempEntitiesWithoutUploadId = {}
const uploadingFilesIdOrNameToTypeMap = {}

interface IFileToBoardMap {
  [key: string]: string
}

interface FileItem {
  options?: {
    uploadId?: string
  }
  currentUpload?: {
    state: {
      isCompleted: boolean
    }
  }
  file?: {
    name: string
  }
  params?: {
    node_id: string
  }
  name?: string
}

const filesToBoardIdMap: IFileToBoardMap = {}
const nameToPreviewUrl: Record<string, string> = {}
const uploadIdToPreviewUrl: Record<string, string> = {}

const clearObject = (obj: Record<string, unknown>) => {
  Object.keys(obj).forEach((key) => delete obj[key])
}

const clearMapsForNewUpload = () => {
  clearObject(filesToBoardIdMap as unknown as Record<string, unknown>)
  // processedFiles.clear()
  clearObject(tempEntitiesWithoutUploadId)
  clearObject(nameToPreviewUrl)
  clearObject(uploadIdToPreviewUrl)
}

const clearAllGlobalUploadState = () => {
  clearMapsForNewUpload()
}

const bindUploaderListEvents = (uploaderListObject: UploaderList, dispatch, getState) => {
  uploaderListObject.removeAllListeners()

  uploaderListObject.on(UPLOADER_LIST_EVENTS.UPDATE, uploaderListUpdateHandler.bind(null, { dispatch, getState }))
  uploaderListObject.on(UPLOADER_LIST_EVENTS.ERROR, uploaderListErrorHandler.bind(null, { dispatch, getState }))
}

const adapter = createEntityAdapter<IExtendedUploadingFile>()

export const filesUploadingSelectors = adapter.getSelectors()

const mapItemToEntity = ({ item, index, isInitial, parentId, nodeId = undefined }): IExtendedUploadingFile => {
  const name = isInitial ? item.name : item.file.name
  const size: string = isInitial ? toMb(item.size) : toMb(item.file.size)
  const type = isInitial ? item.type : item.file.type
  const id = index
  const uploadId = item?.options?.uploadId || null

  let state = UPLOADED_ITEM_INITIAL_STATE
  if (item?.currentUpload?.state) {
    state = { ...item?.currentUpload?.state }
    if (state.error !== null) {
      const { error } = state
      if (error) {
        const { error: { status } } = state
        // if status === 405 it means item has been already uploaded
        if (status !== 405) {
          state.error = {
            response: { status }
          }
        } else {
          state.error = null
          state.isCompleted = true
          state.isError = false
        }
      } else if (error?.message) {
        state.error = { message: error.message }
      }
    }
  }

  let previewUrl: string | undefined

  try {
    if (isInitial && typeof type === 'string' && type.startsWith('image/') && item instanceof File) {
      previewUrl = URL.createObjectURL(item)

      if (name) {
        nameToPreviewUrl[name] = previewUrl
      }
    } else if (!isInitial) {
      if (item?.options?.uploadId && uploadIdToPreviewUrl[item.options.uploadId]) {
        previewUrl = uploadIdToPreviewUrl[item.options.uploadId]
      } else if (name && nameToPreviewUrl[name]) {
        previewUrl = nameToPreviewUrl[name]
      }
    }
  } catch (e) {
    previewUrl = undefined
  }

  return {
    id,
    name,
    size,
    type,
    state,
    uploadedItem: item?.currentUpload?.upload,
    uploadingSourceType: uploadId ? uploadingFilesIdOrNameToTypeMap[uploadId] : uploadingFilesIdOrNameToTypeMap[name] || type,
    uploadId: item?.options?.uploadId || null,
    parentId,
    nodeId,
    previewUrl,
  }
}

export const calcMb = (number) => {
  return Math.trunc(number / Math.pow(1024, 2))
}

const showNotificationsForRejectedFiles = (rejectedFiles) => {
  rejectedFiles.forEach(fileIgnored => {
    // Handle rejected file notifications if needed
  })
}

export const initialState = {
  currentUploadingType: UPLOADER_LIST_TYPES.FILES,
  status: UPLOADING_STATUSES.IDLE,
  listState: {
    uploaded: 0,
    total: 0,
    hasErrors: false,
    isUploading: false,
    isCompleted: false
  },
  duplicateModal: {
    isOpen: false,
    index: 0,
    queue: [] as { fileName: string, fileKey: string, duplicates: SearchNode[] }[],
    decisions: {} as Record<string, { choice: 'cancel' | 'keep_both' | 'replace', target_node_id?: string }>,
    isGeneratingName: false
  }
}

const checkAndGenerateNewFileName = async ({ fileName, parentId, type, userId }: {fileName: string, parentId: string, type: NodeTypes, userId: number}) => {
  const MAX_ATTEMPTS = 100
  let candidateName = generateFilesItemName(fileName)

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    const duplicates = await checkForDuplicateFiles({ fileName: candidateName, parentId, type, userId })
    if (!duplicates.length) {
      return candidateName
    }
    candidateName = generateFilesItemName(candidateName)
  }

  // Fallback: add timestamp to ensure breaking out of potential infinite cycles
  const dotIndex = candidateName.lastIndexOf('.')
  const hasExtension = dotIndex > 0
  const base = hasExtension ? candidateName.slice(0, dotIndex) : candidateName
  const ext = hasExtension ? candidateName.slice(dotIndex) : ''
  const uniqueName = `${base} ${Date.now()}${ext}`
  return uniqueName
}

const processSearchResults = (nodes: SearchNode[], originalFileName?: string) => {
  const isTrashed = (item) => item.linked_node_is_trashed || item.is_trashed
  
  // If originalFileName is provided, filter for exact name matches (case-insensitive)
  let filteredNodes = nodes
  if (originalFileName) {
    filteredNodes = nodes.filter(item => 
      !isTrashed(item) && 
      item.name.toLowerCase() === originalFileName.toLowerCase()
    )
  } else {
    filteredNodes = nodes.filter(item => !isTrashed(item))
  }
  
  const sortByUpdated = (a, b) => new Date(b.updated).getTime() - new Date(a.updated).getTime()
  return filteredNodes.sort(sortByUpdated)
}

const checkForDuplicateFiles = async ({ fileName, parentId, type, userId }: { fileName: string, parentId: string, type: string, userId: number }) => {
  const params = {
    parent_id: parentId,
    labels_or_name_pattern: fileName,
    type,
    limit: 100,
  }
  const response = await searchApi.search(userId, params as any)
  return processSearchResults(response._embedded.nodes, fileName)
}

const tryBatchSearch = async (files: File[], parentId: string, type: string, userId: number) => {
  const searchParams = files.map(file => ({
    parent_id: parentId,
    labels_or_name_pattern: file.name,
    type,
    limit: 100,
  }))

  const batchResponse: BatchedOpSchema = await searchApi.batchSearch(userId, searchParams as any)

  if (batchResponse.state !== 'done' || !batchResponse.responses || !batchResponse.responses.length) {
    throw new Error('Batch operation not completed')
  }

  return batchResponse.responses.map((response, index) => {
    const isSuccessStatus = typeof response.status === 'number' && response.status >= 200 && response.status < 300
    if (!isSuccessStatus || !response.json_body) {
      throw new Error(`Batch response invalid at index ${index}`)
    }

    const nodes = response.json_body?._embedded?.nodes || []
    return processSearchResults(nodes, files[index].name)
  })
}

const fallbackToSequentialSearch = async (files: File[], parentId: string, type: string, userId: number) => {
  const results = []
  for (const file of files) {
    const duplicates = await checkForDuplicateFiles({
      fileName: file.name,
      parentId,
      type,
      userId
    })
    results.push(duplicates)
  }
  return results
}

const checkForDuplicateFilesBatch = async ({ files, parentId, type, userId }: { files: File[], parentId: string, type: string, userId: number }) => {
  if (files.length === 0) return []

  // Для одного файла используем обычный API
  if (files.length === 1) {
    const result = await checkForDuplicateFiles({ fileName: files[0].name, parentId, type, userId })
    return [result]
  }

  try {
    return await tryBatchSearch(files, parentId, type, userId)
  } catch (error) {
    console.warn('Batch search failed, falling back to sequential requests:', error)
    return await fallbackToSequentialSearch(files, parentId, type, userId)
  }
}

export const checkFilesForRenameThunk = createAsyncThunk(
  'filesUploading/checkFilesForRenameThunk',
  async ({ files, type }: { files: FileList, type: string }, { getState, dispatch }) => {
    const filesArray = Array.from(files)
    const state = getState() as RootState
    const userId = state.user.user?.family_user_id
    const searchParentId = state.user.assetRootBoardId
    try {
      if (!searchParentId) {
        console.warn('Duplicate search skipped: missing parent identifier')
        return []
      }

      const duplicates = await checkForDuplicateFilesBatch({ files: filesArray, parentId: searchParentId, type, userId })

      if (duplicates.length) {
        const queue = filesArray.map((file, idx) => ({
          fileName: file.name,
          fileKey: `${file.name}:${file.size}:${(file as any).lastModified || 0}`,
          duplicates: Array.isArray(duplicates[idx]) ? duplicates[idx] : []
        }))
          .filter(item => item.duplicates.length)

        if (queue.length) {
          dispatch(actions.setDuplicateModal({ queue }))
          dispatch(openModal({ type: BoardToolbarAction.ResolveDuplicates, context: 'asset' }))
        }
      }
      return duplicates
    } catch (_) {
      showNotification({
        type: NOTIFICATION_TYPES.ERROR,
        title: 'Duplicate check failed',
        message: 'Could not check duplicates. Please try again later.'
      })
      return null
    }
  })

export const uploadFilesThunk = createAsyncThunk(
  'filesUploading/uploadFilesThunk',
  async ({ files, callback, modificators = [], symlinkParentId = '', uploadingSourceType = UPLOADER_LIST_TYPES.FILES }:
    { files: FileList, assetsRootBoardId?: string, callback?: () => void, modificators?: any[], symlinkParentId: string, uploadingSourceType?: UPLOADER_LIST_TYPES },
  { dispatch, getState }) => {
    const filesSdk = getFilesSdk()
    const state = getState() as RootState
    const userId = state.user.user?.family_user_id
    const duplicateSearchParentId = state.user.assetRootBoardId
    const { boardsService } = filesSdk
    const rejectedFiles: File[] = []
    // Will split into two groups: normal uploads and versioned uploads
    const normalAcceptedFiles: File[] = []
    const normalModificators: Record<string, any>[] = []
    const versionAcceptedFiles: File[] = []
    const versionModificators: Record<string, any>[] = []
    const maxFileSize = Number(state.user.maxFileSize)
    const decisions = state.filesUploading.duplicateModal.decisions as Record<string, { choice: 'cancel' | 'keep_both' | 'replace', target_node_id?: string }>
    dispatch(actions.setCurrentUploadingType(uploadingSourceType))

    const filesArray = Array.from(files)
    // decisions now keyed by stable fileKey

    for (let index = 0; index < filesArray.length; index++) {
      const file = filesArray[index]
      if (!!maxFileSize && file.size > maxFileSize) {
        showNotification({
          type: NOTIFICATION_TYPES.WARNING,
          title: 'File size error',
          message: 'File size is too large'
        })
      } else {
        let newFile = file
        const fileKey = `${file.name}:${file.size}:${(file as any).lastModified || 0}`
        uploadingFilesIdOrNameToTypeMap[file.name] = uploadingSourceType

        const decisionEntry = decisions[fileKey]
        if (decisionEntry) {
          if(decisionEntry.choice === DuplicateActionChoice.Cancel) {
            continue
          }
          if (decisionEntry.choice === DuplicateActionChoice.KeepBoth) {
            dispatch(actions.setDuplicateNameGeneration(true))
            try {
              const newFileName = await checkAndGenerateNewFileName({ fileName: file.name, parentId: duplicateSearchParentId, type: NodeTypes.FILE, userId } as any)
              const blob = file.slice(0, file.size, file.type)
              newFile = new File([blob], newFileName, { type: file.type })
              normalAcceptedFiles.push(newFile)
              normalModificators.push(modificators[index] || {})
              filesToBoardIdMap[file.name] = symlinkParentId
            } catch (error) {
              console.error('Failed to generate unique file name', error)
              showNotification({
                type: NOTIFICATION_TYPES.ERROR,
                title: 'Could not prepare file name',
                message: 'Unable to generate a new name. Please try again later.'
              })
            } finally {
              dispatch(actions.setDuplicateNameGeneration(false))
            }
            continue
          }

          if (decisionEntry.choice === DuplicateActionChoice.Replace) {
            const targetNodeId = decisionEntry.target_node_id
            if (targetNodeId) {
              versionAcceptedFiles.push(newFile)
              versionModificators.push({ ...(modificators[index] || {}), target_node_id: targetNodeId })
              filesToBoardIdMap[file.name] = symlinkParentId
              continue
            }
          }
        }
        normalAcceptedFiles.push(newFile)
        normalModificators.push(modificators[index] || {})
        filesToBoardIdMap[file.name] = symlinkParentId
      }
    }

    dispatch(actions.resetDuplicateModal())

    showNotificationsForRejectedFiles(rejectedFiles)

    if (!normalAcceptedFiles.length && !versionAcceptedFiles.length) {
      dispatch(closeModal())
      return
    }

    dispatch(closeModal())
    dispatch(actions.updateStatus(UPLOADING_STATUSES.IN_PROGRESS))
    dispatch(actions.updateItems({
      entities: [
        ...normalAcceptedFiles.map((elem, index) =>
          mapItemToEntity({ item: elem, index, isInitial: true, parentId: symlinkParentId })),
        ...versionAcceptedFiles.map((elem, index) =>
          mapItemToEntity({ item: elem, index: normalAcceptedFiles.length + index, isInitial: true, parentId: symlinkParentId }))
      ]
    }))

    // Start normal uploads
    if (normalAcceptedFiles.length > 0) {
      const uploaderListObject = await boardsService.addToUploadList(
        converFileListToFileObjectList(normalAcceptedFiles, normalModificators),
        { multipartUpload: false, completeDelay: 400, uploadBatchSize: UPLOAD_BATCH_SIZE },
        { parent_id: symlinkParentId }
      )

      bindUploaderListEvents(uploaderListObject as any, dispatch, getState)

      const { state: listState } = uploaderListObject.getList()

      if (!listState.isUploading || listState.isCompleted) {
        uploaderListObject.start()
      }

      if (!filesUploaderListObject) {
        filesUploaderListObject = uploaderListObject as any
      }
    }

    // Start version uploads as a separate batch targeting existing nodes
    if (versionAcceptedFiles.length > 0) {
      const versionsUploaderListObject = await boardsService.addToUploadList(
        converFileListToFileObjectList(versionAcceptedFiles, versionModificators),
        { multipartUpload: false, completeDelay: 400, uploadBatchSize: UPLOAD_BATCH_SIZE },
        { parent_id: undefined as any }
      )

      bindUploaderListEvents(versionsUploaderListObject as any, dispatch, getState)

      const { state: versionsListState } = versionsUploaderListObject.getList()

      if (!versionsListState.isUploading || versionsListState.isCompleted) {
        versionsUploaderListObject.start()
      }

      if (!filesUploaderListObject) {
        filesUploaderListObject = versionsUploaderListObject as any
      }
    }

    if (callback) {
      callback()
    }
  }
)

export const uploadAiAssistantFilesThunk = createAsyncThunk(
  'filesUploading/uploadAiAssistantFilesThunk',
  async (
    {
      files,
      callback,
      modificators = [],
      symlinkParentId = '',
      uploadingSourceType = UPLOADER_LIST_TYPES.FILES
    }: {
      files: FileList,
      assetsRootBoardId?: string,
      callback?: () => void,
      modificators?: any[],
      symlinkParentId: string,
      uploadingSourceType?: UPLOADER_LIST_TYPES
    },
    { dispatch, getState }
  ) => {
    const filesSdk = getFilesSdk()
    const state = getState() as RootState
    const rejectedFiles: File[] = []
    const acceptedFiles: File[] = []
    const maxFileSize = Number(state.user.maxFileSize)

    dispatch(actions.setCurrentUploadingType(uploadingSourceType))

    const filesArray = Array.from(files)

    filesArray.forEach(file => {
      if (!!maxFileSize && file.size > maxFileSize) {
        showNotification({
          type: NOTIFICATION_TYPES.WARNING,
          title: 'File size error',
          message: 'File size is too large'
        })
      } else {
        acceptedFiles.push(file)
        uploadingFilesIdOrNameToTypeMap[file.name] = uploadingSourceType
        filesToBoardIdMap[file.name] = symlinkParentId
      }
    })

    showNotificationsForRejectedFiles(rejectedFiles)

    if (!acceptedFiles.length) {
      return
    }

    dispatch(actions.updateStatus(UPLOADING_STATUSES.IN_PROGRESS))
    dispatch(actions.updateItems({
      entities: acceptedFiles.map((elem, index) =>
        mapItemToEntity({ item: elem, index, isInitial: true, parentId: symlinkParentId }))
    }))

    const service = filesSdk.familyFolderOperationsService

    const uploaderListObject = await service.addToUploadList(
      converFileListToFileObjectList(acceptedFiles, modificators),
      { multipartUpload: false, completeDelay: 400, uploadBatchSize: UPLOAD_BATCH_SIZE },
      { parent_id: symlinkParentId }
    )

    bindUploaderListEvents(uploaderListObject as any, dispatch, getState)

    const { state: listState } = uploaderListObject.getList()

    if (!listState.isUploading || listState.isCompleted) {
      uploaderListObject.start()
    }

    if (callback) {
      callback()
    }

    if (filesUploaderListObject) {
      return
    }

    filesUploaderListObject = uploaderListObject as any
  }
)

export const uploadSharedFilesThunk = createAsyncThunk(
  'filesUploading/uploadSharedFilesThunk',
  async ({ files, parentId, callback, modificators = [], sharedId, uploadingSourceType }:
    { files: FileList, parentId?: string, callback?: () => void, modificators?: any[], sharedId: string, uploadingSourceType?: UPLOADER_LIST_TYPES },
  { dispatch, getState }) => {
    // Clear global maps/sets before starting a new shared upload session
    clearMapsForNewUpload()
    const filesSdk = getFilesSdk()
    const state = getState() as RootState
    const rejectedFiles = []
    const acceptedFiles = []
    const maxFileSize = Number(state.user.maxFileSize)

    dispatch(actions.setCurrentUploadingType(uploadingSourceType ?? UPLOADER_LIST_TYPES.SHARED_WITH_ME_FILES))

    const filesArray = Array.from(files)

    filesArray.forEach(file => {
      if (!!maxFileSize && file.size > maxFileSize) {
        showNotification({
          type: NOTIFICATION_TYPES.WARNING,
          title: 'File size error',
          message: 'File size is too large'
        })
      } else {
        acceptedFiles.push(file)
        uploadingFilesIdOrNameToTypeMap[file.name] = uploadingSourceType
      }
    })

    showNotificationsForRejectedFiles(rejectedFiles)

    if (!acceptedFiles.length) {
      return
    }

    dispatch(actions.updateStatus(UPLOADING_STATUSES.IN_PROGRESS))
    dispatch(actions.updateItems({
      entities: acceptedFiles.map((elem, index) =>
        mapItemToEntity({ item: elem, index, isInitial: true, parentId }))
    }))

    acceptedFiles.forEach(file => {
      tempEntitiesWithoutUploadId[file.name] = uploadingSourceType
    })

    const uploaderListObject = await filesSdk.notMyBoardsService.addToUploadList(
      sharedId,
      converFileListToFileObjectList(acceptedFiles, modificators),
      { multipartUpload: true, completeDelay: 400, uploadBatchSize: UPLOAD_BATCH_SIZE },
      { parent_id: parentId }
    )

    bindUploaderListEvents(uploaderListObject as any, dispatch, getState)

    const { state: listState } = uploaderListObject.getList()

    if (!listState.isUploading || listState.isCompleted) {
      uploaderListObject.start()
    }

    if (callback) {
      callback()
    }

    if (sharedFilesUploaderListObject) {
      return
    }

    sharedFilesUploaderListObject = uploaderListObject as any
  }
)

async function uploaderListUpdateHandler({ dispatch, getState }, { list, state }) {
  const user = getState().user
  const familyUserId = user.user?.family_user_id
  const uploadingSourceType = getState().filesUploading.currentUploadingType
  const currentBoardId = getState().boards.currentBoardId
  const fileItems = list as FileItem[]
  const location = window.location.pathname
  const isShared = !!getState().publicLinkFiles.sharedId

  fileItems.forEach(item => {
    const uploadId = item?.options?.uploadId
    const fileName = item?.file?.name || item?.name

    if (uploadId && !uploadingFilesIdOrNameToTypeMap[uploadId]) {
      uploadingFilesIdOrNameToTypeMap[uploadId] = uploadingFilesIdOrNameToTypeMap[fileName]
    }

    if (uploadId && fileName && filesToBoardIdMap[fileName]) {
      filesToBoardIdMap[uploadId] = filesToBoardIdMap[fileName]
    } else if (uploadId && fileName) {
      // Try to find parentId by matching base filename (without numbers in parentheses)
      const baseFileName = fileName.replace(/\s*\(\d+\)/, '') // Remove " (N)" pattern
      if (filesToBoardIdMap[baseFileName]) {
        filesToBoardIdMap[uploadId] = filesToBoardIdMap[baseFileName]
        filesToBoardIdMap[fileName] = filesToBoardIdMap[baseFileName] // Also save under current name
      }
    }

    if (uploadId && fileName && nameToPreviewUrl[fileName] && !uploadIdToPreviewUrl[uploadId]) {
      uploadIdToPreviewUrl[uploadId] = nameToPreviewUrl[fileName]
    }
  })

  if (state.isFinished && isShared && uploadingSourceType !== UPLOADER_LIST_TYPES.VERSIONS) {
    dispatch(fetchFilesPublicLinkItems({ id: currentBoardId }))
    return
  }

  if (state.isFinished && familyUserId && uploadingSourceType !== UPLOADER_LIST_TYPES.VERSIONS) {
    if (location === ROUTES.dashboard.recentlyUploaded) {
      dispatch(fetchFirstRecentlyUploadedThunk())
    } else if (currentBoardId) {
      // not to fectch if
      // dispatch(fetchBoardAllAssetsThunk({ boardId: currentBoardId }))
    } else {
      // dispatch(fetchFirstAssetsThunk())
    }
  }

  const entities = list.map((elem, index) => {
    const uploadId = elem?.options?.uploadId
    const fileName = elem?.file?.name || elem?.name
    const nodeId = elem?.params?.node_id
    // Try to get parentId by uploadId first, then fallback to fileName
    const parentId = filesToBoardIdMap[uploadId] || filesToBoardIdMap[fileName]


    return mapItemToEntity({ item: elem, index, isInitial: false, parentId, nodeId })
  })

  const mergedEntities = entities.map((entity) => {
    return { ...entity, previewUrl: nameToPreviewUrl[entity.name] }
  })

  dispatch(actions.updateItems({ entities: mergedEntities }))
  dispatch(actions.updateListState(state))

  const allEntities = adapter.getSelectors().selectAll((getState() as RootState).filesUploading)
  const filesInProgressExist = allEntities.some(file => !file.state.isCompleted)

  if (!filesInProgressExist) {
    // commented out to prevent closing after uploading all items
    // dispatch(actions.updateStatus(UPLOADING_STATUSES.IDLE))
  }
}

function uploaderListErrorHandler({ dispatch, getState }, { error: errorIgnored }) {
  const listState = getState().filesUploading.listState

  dispatch(actions.updateListState({
    ...listState,
    hasErrors: true,
  }))
}

export const removeFileFromUploadingThunk = createAsyncThunk(
  'filesUploading/removeFileFromUploadingThunk',
  async (file: IUploadingFile, { getState }) => {
    const state = getState() as RootState
    const isShared = !!state.publicLinkFiles.sharedId

    if (isShared) {
      if (sharedFilesUploaderListObject) {
        const listFromUploader = sharedFilesUploaderListObject.getList().list
        await sharedFilesUploaderListObject.remove(listFromUploader[file.id] as File | UploaderBase)
      }
    } else {
      if (filesUploaderListObject) {
        const listFromUploader = filesUploaderListObject.getList().list
        await filesUploaderListObject.remove(listFromUploader[file.id] as File | UploaderBase)
      }
    }
  }
)

export const reloadFileUploadingThunk = createAsyncThunk(
  'filesUploading/reloadFileUploadingThunk',
  async (_, { getState }) => {
    const state = getState() as RootState
    const isShared = !!state.publicLinkFiles.sharedId

    if (isShared) {
      if (sharedFilesUploaderListObject) {
        await sharedFilesUploaderListObject.resume()
      }
    } else {
      if (filesUploaderListObject) {
        await filesUploaderListObject.resume()
      }
    }
  }
)

export const resumeFileUploadingThunk = createAsyncThunk(
  'filesUploading/resumeFileUploadingThunk',
  async (_, { getState }) => {
    const state = getState() as RootState
    const isShared = !!state.publicLinkFiles.sharedId

    if (isShared) {
      if (sharedFilesUploaderListObject) {
        await sharedFilesUploaderListObject.resume()
      }
    } else {
      if (filesUploaderListObject) {
        await filesUploaderListObject.resume()
      }
    }
  }
)

export const restartFileUploadingThunk = createAsyncThunk(
  'filesUploading/restartFileUploadingThunk',
  async (item: any, { getState }) => {
    const state = getState() as RootState
    const isShared = !!state.publicLinkFiles.sharedId

    if (isShared) {
      if (sharedFilesUploaderListObject) {
        const listFromUploader = sharedFilesUploaderListObject.getList().list
        await sharedFilesUploaderListObject.restart(listFromUploader[item.id] as UploaderBase)
      }
    } else {
      if (filesUploaderListObject) {
        const listFromUploader = filesUploaderListObject.getList().list
        await filesUploaderListObject.restart(listFromUploader[item.id] as UploaderBase)
      }
    }
  }
)

export const closeWidgetThunk = createAsyncThunk(
  'filesUploading/closeWidgetThunk',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState
    const isShared = !!state.publicLinkFiles.sharedId

    if (isShared) {
      if (sharedFilesUploaderListObject) {
        sharedFilesUploaderListObject.cancel()
        sharedFilesUploaderListObject.clear()
        sharedFilesUploaderListObject.removeAllListeners()
      }
    } else {
      if (filesUploaderListObject) {
        filesUploaderListObject.cancel()
        filesUploaderListObject.clear()
        filesUploaderListObject.removeAllListeners()
      }
    }

    // Clear all upload-related global state on close
    clearAllGlobalUploadState()

    filesUploaderListObject = undefined
    sharedFilesUploaderListObject = undefined

    dispatch(actions.updateStatus(UPLOADING_STATUSES.IDLE))
  }
)

export const filesUploadingSlice = createSlice({
  name: 'filesUploading',
  initialState: adapter.getInitialState(initialState),
  reducers: {
    updateItems: (state, action) => {
      adapter.setAll(state, action.payload.entities)
    },
    clear: (state) => {
      adapter.removeAll(state)
    },
    removeItem: (state, action) => {
      adapter.removeOne(state, action.payload)
    },
    updateStatus: (state, action) => {
      state.status = action.payload
    },
    updateListState: (state, action) => {
      state.listState = action.payload
    },
    setCurrentUploadingType: (state, action) => {
      state.currentUploadingType = action.payload
    },
    setDuplicateModal: (state, action) => {
      state.duplicateModal.index = 0
      state.duplicateModal.queue = action.payload.queue
      state.duplicateModal.decisions = {}
      state.duplicateModal.isGeneratingName = false
    },
    resetDuplicateModal: (state) => {
      state.duplicateModal.index = 0
      state.duplicateModal.queue = []
      state.duplicateModal.decisions = {}
      state.duplicateModal.isGeneratingName = false
    },
    setDuplicateNameGeneration: (state, action) => {
      state.duplicateModal.isGeneratingName = action.payload
    },
    resolveDuplicateChoice: (state, action) => {
      const current = state.duplicateModal.queue[state.duplicateModal.index]
      if (current) {
        const { choice, target_node_id } = action.payload as {
          choice: 'cancel' | 'keep_both' | 'replace'
          target_node_id?: string
        }

        const fallbackTargetId = current.duplicates?.[0]?.id as string | undefined
        const resolvedTargetId = choice === 'replace' ? (target_node_id || fallbackTargetId) : undefined

        state.duplicateModal.decisions[current.fileKey] = { choice, target_node_id: resolvedTargetId }

        const nextIndex = state.duplicateModal.index + 1
        if (nextIndex < state.duplicateModal.queue.length) {
          state.duplicateModal.index = nextIndex
        }
      }
    }
  },
})

const {
  reducer, actions
} = filesUploadingSlice

export default filesUploadingSlice.reducer

export { actions as filesUploadingActions, reducer as filesUploadingReducer }
